import { Fragment } from 'react';
import { useFragment } from '@apollo/client/react';
import { plural, select, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, List, ListHeader, ListItem, TeamAvatar, Typography, UserAvatar } from '@pocketlaw/tetris';
import { AclAction } from 'shared/domains/apollo/generated/types';
import { AclChange } from './AclChange';
import { MoveEntryAclChangesFragmentDoc } from './MoveEntryACLChanges.gql';
import { calculateACLDiff } from './calculateACLDiff';
export function MoveEntryACLChanges(props) {
    const { fromSource, fromTarget } = props;
    const source = useFragment({
        from: fromSource,
        fragment: MoveEntryAclChangesFragmentDoc,
    });
    const target = useFragment({
        from: fromTarget || {},
        fragment: MoveEntryAclChangesFragmentDoc,
    });
    if (!source.complete || !target.complete) {
        return null;
    }
    const { acls: sourceAcls, teamAcls: sourceTeamAcls, name: sourceName } = source.data || {};
    const { acls: targetAcls, teamAcls: targetTeamAcls, name: originalTargetName } = target.data || {};
    const usersDiff = calculateACLDiff(sourceAcls, targetAcls, 'userId');
    const teamsDiff = calculateACLDiff(sourceTeamAcls, targetTeamAcls, 'teamId');
    const hasUserChanges = usersDiff.length > 0;
    const hasTeamChanges = teamsDiff.length > 0;
    const hasAclChanges = hasUserChanges || hasTeamChanges;
    const targetName = (originalTargetName === null || originalTargetName === void 0 ? void 0 : originalTargetName.length)
        ? originalTargetName
        : t({ message: 'root', comment: 'Root folder label' });
    const getTeamSubtitle = (count = 0) => t({
        message: plural(count, {
            one: '# member',
            other: '# members',
        }),
        comment: 'Filesystem access drawer list item subtitle for teams',
    });
    const deletedUser = t({
        message: 'Deleted user',
        comment: 'Filesystem access drawer list item title for deleted users',
    });
    const sourceKind = source.data.kind;
    const confirmationText = select(sourceKind, {
        DOCUMENT: `Moving ${sourceName} to ${targetName} will apply the following access changes to the document.`,
        FOLDER: `Moving ${sourceName} to ${targetName} will apply the following access changes to the folder.`,
        other: 'Moving this item will apply the following access changes to the moved item.',
    });
    if (!hasAclChanges) {
        return (<Typography $lineHeight="neutralized">
        <Trans comment="Confirm message for quick move document dialog">
          You are moving <b>{sourceName}</b> to <b>{targetName}</b>.
        </Trans>
      </Typography>);
    }
    return (<Fragment>
      <Typography>{confirmationText}</Typography>

      <List>
        {hasTeamChanges && (<Fragment>
            <ListHeader title="Teams"/>
            {teamsDiff
                .filter((teamAcl) => !teamAcl.team.isProjectTeam)
                .filter((acl) => !acl.actions.next.includes(AclAction.Traverse))
                .map(({ id, actions, team }) => (<ListItem key={id} disabled>
                  <TeamAvatar size="md" title={team.name} subtitle={getTeamSubtitle(team === null || team === void 0 ? void 0 : team.members.length)}/>
                  <Box ml="auto" alignItems="center" gap="md">
                    <AclChange actions={actions}/>
                  </Box>
                </ListItem>))}
          </Fragment>)}

        {hasUserChanges && (<Fragment>
            <ListHeader title="Users"/>
            {usersDiff.map(({ id, actions, user }) => (<ListItem key={id} disabled>
                <UserAvatar size="md" title={(user === null || user === void 0 ? void 0 : user.name) || deletedUser} subtitle={(user === null || user === void 0 ? void 0 : user.email) || ''}/>
                <Box ml="auto" alignItems="center" gap="md">
                  <AclChange actions={actions}/>
                </Box>
              </ListItem>))}
          </Fragment>)}
      </List>
    </Fragment>);
}
